import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import { tap, switchMap } from "rxjs/operators";
import { AuthService } from '../../../services/auth.service';
import { ContactService, ContactSearchSpec } from '../../../admin/services/contacts.service';
import { DialogService } from "../../../common/services/dialog.service";
import { LoaderService } from '../../services/loader.service';

import { FinancialAssuranceService } from '../../services/financial-assurance.service';

import { FinancialAssuranceSummary, FinancialInstrument, FinancialInstrumentType, FinancialStatement } from '../../models/financialAssurance.model';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'fa-statement-management',
    styleUrls: ['./fa-statement-management.component.scss'],
    templateUrl: './fa-statement-management.component.html',
    standalone: false
})

export class FinancialStatementManagementComponent implements OnInit, OnDestroy {

  @Input() externalView: boolean = true;
  @Input() orgId: string;
  @Input() faSummary: Observable<FinancialAssuranceSummary>;

  //public typeDescriptions = FinancialAssuranceService.TypeDescriptions;
    public typeDescriptions: any[];
    public subscriptions: Subscription = new Subscription();
    public activeFinancialStatement: FinancialStatement;
    public isCompliant: boolean;
    public editEnabled: boolean = false;
    public isInEditMode: boolean = false;
    public hasFirstConfirmationDate: boolean = false;
    public editButtonText: string = "Edit";
    public orgName: string = '';
    public requiredBondingAmount: string = 'Calculating...';
    public providedBondingAmount: string = 'Calculating...';
    public bondingDecisionBasis: string = "";
    public expirationDate: string;
    public lastReviewedDate: string;
    public firstReceiptConfirmationDate: string;
    public secondReceiptConfirmationDate: string;
    public lastReviewedDateEntryDisplayName: string
    public firstReceiptConfirmationEntryDisplayName: string
    public secondReceiptConfirmationEntryDisplayName: string

    constructor(
        public financialAssuranceService: FinancialAssuranceService,
        public currencyPipe: CurrencyPipe,
        public datePipe: DatePipe,
        public readonly loader: LoaderService,
        public authService: AuthService,
        public contactService: ContactService,
        public dialogService: DialogService
    ) { }

    ngOnInit() {
        this.loadData();
    }

    ngOnDestroy() {
        if (this.subscriptions != null) this.subscriptions.unsubscribe();
    }

    public loadData(): void {
        let obs$: Observable<any> = this.financialAssuranceService.TypeDescriptions.pipe(
            tap((data: any[]) => {
                this.typeDescriptions = data;
            }),
            switchMap(() => {
                return this.financialAssuranceService.getActiveFinancialStatement(this.orgId)
            }),
            switchMap((data) => {
                this.activeFinancialStatement = data;
                let contactIds: string[] = [];
                if (this.activeFinancialStatement != null) {
                    this.providedBondingAmount = this.currencyPipe.transform(this.activeFinancialStatement.amount);
                    this.editEnabled = true;
                    
                    if (this.activeFinancialStatement.lastReviewedDateReviewerUserId)
                        contactIds.push(this.activeFinancialStatement.lastReviewedDateReviewerUserId);
                    if (this.activeFinancialStatement.firstConfirmationUserId)
                        contactIds.push(this.activeFinancialStatement.firstConfirmationUserId);
                    if (this.activeFinancialStatement.secondConfirmationUserId)
                        contactIds.push(this.activeFinancialStatement.secondConfirmationUserId);
                }
                if (contactIds.length > 0) return this.contactService.getAll({ ids: contactIds } as ContactSearchSpec);
                else return of([]);
            }),
            tap((contacts: any) => {
                if (contacts != null && contacts.length > 0) {
                    let contact: any;
                    if (this.activeFinancialStatement.lastReviewedDateReviewerUserId != null && this.activeFinancialStatement.lastReviewedDateReviewerUserId.length > 0) {
                        contact = contacts.find(c => c.id == this.activeFinancialStatement.lastReviewedDateReviewerUserId);
                        if (contact != null) this.lastReviewedDateEntryDisplayName = contact.displayName;
                    }
                    if (this.activeFinancialStatement.firstConfirmationUserId != null && this.activeFinancialStatement.firstConfirmationUserId.length > 0) {
                        contact = contacts.find(c => c.id == this.activeFinancialStatement.firstConfirmationUserId);
                        if (contact != null) this.firstReceiptConfirmationEntryDisplayName = contact.displayName;
                    }
                    if (this.activeFinancialStatement.secondConfirmationUserId != null && this.activeFinancialStatement.secondConfirmationUserId.length > 0) {
                        contact = contacts.find(c => c.id == this.activeFinancialStatement.secondConfirmationUserId);
                        if (contact != null) this.secondReceiptConfirmationEntryDisplayName = contact.displayName;
                    }
                }
            })
        );

        let data$: Subscription = obs$.subscribe(
            () => {
                if (this.activeFinancialStatement.lastReviewedDate) {
                    this.lastReviewedDate = this.dateFormatter(this.activeFinancialStatement.lastReviewedDate);
                }

                if (this.activeFinancialStatement.confirmedDateThatFirstRequestForUpdateWasReceived) {
                    this.firstReceiptConfirmationDate = this.dateFormatter(this.activeFinancialStatement.confirmedDateThatFirstRequestForUpdateWasReceived);
                    this.hasFirstConfirmationDate = true;
                }

                if (this.activeFinancialStatement.confirmedDateThatSecondRequestForUpdateWasReceived)
                    this.secondReceiptConfirmationDate = this.dateFormatter(this.activeFinancialStatement.confirmedDateThatSecondRequestForUpdateWasReceived);

                if (this.activeFinancialStatement.expirationDate)
                    this.expirationDate = this.dateFormatter(this.activeFinancialStatement.expirationDate);
                this.loader.toggleLoader(false);
            },
            (error) => {
                console.error(error);
                this.loader.toggleLoader(false);
            }
        )
        this.subscriptions.add(data$);


        var summarySubscription$ = this.faSummary.pipe(finalize(() => { this.subscriptions.add(summarySubscription$); })
            ).subscribe(
                summary => {
                    this.bondingDecisionBasis = summary.bondingDecisionBasis;
                    this.orgName = summary.organizationName;
                    let minimumRequiredFinancialStatementAmount = summary.requiredCumulativeFinancialInstruments.filter(a => a.typeCode == FinancialInstrumentType.FinancialStatement);
                    if (minimumRequiredFinancialStatementAmount.length > 0) {
                        this.requiredBondingAmount = this.currencyPipe.transform(minimumRequiredFinancialStatementAmount[0].amount);
                    }
                    else {
                        this.requiredBondingAmount = this.currencyPipe.transform(0);
                    }
                    this.isCompliant = summary.isInComplianceForBonding && summary.isInComplianceForInsurance
                },
                error => { console.log(error); alert("ERROR Fetching Not Submitted Financial Assurance Applications"); }
            );
    }

  public composeInfo(summary: FinancialAssuranceSummary): string {
    let info = summary.bondingDecisionBasis;

    if (summary.requiredCumulativeFinancialInstruments) {
      const lowestOption = this.financialAssuranceService.getLowestCumulativeBondRequirement(summary);
      const otherOptions: FinancialInstrument[] = summary.requiredCumulativeFinancialInstruments.filter(x => x != lowestOption);

      if (otherOptions.length) {
        info += `In addition to the minimum amount featured here, you have the following options for satisfying your bonding requirements: `;

        otherOptions.forEach(option => {
          if (option.typeCode === FinancialInstrumentType.FinancialStatement)
            info += `<br/><br/>If you have an existing active Financial Statement the required updated financial statement net worth must be greater or equal to  ${this.currencyPipe.transform(option.amount)}`;
          else
            info += `<br/><br/>${this.currencyPipe.transform(option.amount)} - ${this.getInstrumentTypeDescription(option.typeCode)}`;
        });
      }
    }

    return info;
  }

  public getInstrumentTypeDescription(typeCode: string): string {
    return this.typeDescriptions.find(x => x.id.toLowerCase() === typeCode.toLowerCase()).description;
  }

  public editModeToggle(): void {
    this.isInEditMode = !this.isInEditMode;
    this.editButtonText = this.isInEditMode ? "Cancel Edit" : "Edit";
    if (!this.isInEditMode) {
      this.lastReviewedDate = this.dateFormatter(this.activeFinancialStatement.lastReviewedDate);
      this.firstReceiptConfirmationDate = this.dateFormatter(this.activeFinancialStatement.confirmedDateThatFirstRequestForUpdateWasReceived);
      this.secondReceiptConfirmationDate = this.dateFormatter(this.activeFinancialStatement.confirmedDateThatSecondRequestForUpdateWasReceived);
    }
  }

  public dateFormatter(date: Date): any {
    let formattedDate = this.datePipe.transform(date, 'MM/dd/yyyy');
    return formattedDate;
  }

  public save(): void {
    const dialogMessage = "Once either receipt confirmation date has been saved, they can not be altered until the next review, are you sure you want to save with the current date(s)?"
    this.dialogService.openDialog('confirmCancel', dialogMessage);
  }

  public onDialogConfirm(): void {
    this.dialogService.closeDialog();
    this.loader.toggleLoader(true);
    if (this.lastReviewedDate) {
      let newDate = new Date(this.lastReviewedDate);
      //The methods don't come along with the date on the retrieved financialstatement
      let existingDate = new Date(this.dateFormatter(this.activeFinancialStatement.lastReviewedDate));

      //if the lastReviewed date is updated, null out the receipt confirmation dates
      if (existingDate.getDay() != newDate.getUTCDay()
        || existingDate.getMonth() != newDate.getUTCMonth()
        || existingDate.getFullYear() != newDate.getUTCFullYear())
      {
        this.firstReceiptConfirmationDate = null;
        this.activeFinancialStatement.confirmedDateThatFirstRequestForUpdateWasReceived = null;
        this.secondReceiptConfirmationDate = null;
        this.activeFinancialStatement.confirmedDateThatSecondRequestForUpdateWasReceived = null;

        this.activeFinancialStatement.lastReviewedDate = new Date(this.lastReviewedDate);
        this.activeFinancialStatement.lastReviewedDateReviewerUserId = this.authService.User.id;
        this.lastReviewedDateEntryDisplayName = this.authService.User.displayName;
      }
    }
    if (this.firstReceiptConfirmationDate && this.firstReceiptConfirmationDate != this.dateFormatter(this.activeFinancialStatement.confirmedDateThatFirstRequestForUpdateWasReceived))
    {
      this.activeFinancialStatement.confirmedDateThatFirstRequestForUpdateWasReceived = new Date(this.firstReceiptConfirmationDate);
      this.activeFinancialStatement.firstConfirmationUserId = this.authService.User.id;
      this.firstReceiptConfirmationEntryDisplayName = this.authService.User.displayName;
    }
    if (this.secondReceiptConfirmationDate && this.secondReceiptConfirmationDate != this.dateFormatter(this.activeFinancialStatement.confirmedDateThatSecondRequestForUpdateWasReceived))
    {
      this.activeFinancialStatement.confirmedDateThatSecondRequestForUpdateWasReceived = new Date(this.secondReceiptConfirmationDate);
      this.activeFinancialStatement.secondConfirmationUserId = this.authService.User.id;
      this.secondReceiptConfirmationEntryDisplayName = this.authService.User.displayName;
    }

    this.subscriptions.add(this.financialAssuranceService.saveFinancialStatement(this.activeFinancialStatement).subscribe((financialStatement) => {
      this.loadData();
    }, (error) => {
      console.log(error);
    }));
  }

  public onDialogClose(): void {
    this.dialogService.closeDialog();
  }

}
